import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Plus, 
  BookOpen,
  Dog,
  Scale,
  Activity,
  Clock,
  Trash2
} from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';

function MealPlans() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [mealPlans, setMealPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [foods, setFoods] = useState([]);
  const [formData, setFormData] = useState({
    pet_name: '',
    pet_weight: '',
    pet_age_months: '',
    activity_level: 'moderate',
    special_needs: '',
    foods: []
  });

  useEffect(() => {
    if (user) {
      fetchMealPlans();
      fetchFoods();
    }
  }, [user]);

  const fetchMealPlans = async () => {
    try {
      const response = await api.get('/nutrition/meal-plans');
      setMealPlans(response.data);
    } catch (error) {
      console.error('Error fetching meal plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFoods = async () => {
    try {
      const response = await api.get('/nutrition/foods');
      setFoods(response.data);
    } catch (error) {
      console.error('Error fetching foods:', error);
    }
  };

  const handleCreateMealPlan = async (e) => {
    e.preventDefault();
    try {
      const response = await api.post('/nutrition/meal-plans', formData);
      setMealPlans([response.data, ...mealPlans]);
      setShowCreateForm(false);
      resetForm();
    } catch (error) {
      console.error('Error creating meal plan:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      pet_name: '',
      pet_weight: '',
      pet_age_months: '',
      activity_level: 'moderate',
      special_needs: '',
      foods: []
    });
  };

  const addFoodToMealPlan = () => {
    setFormData({
      ...formData,
      foods: [...formData.foods, { food_id: '', meal_time: 'morning', amount: '', notes: '' }]
    });
  };

  const updateFood = (index, field, value) => {
    const updatedFoods = [...formData.foods];
    updatedFoods[index][field] = value;
    setFormData({ ...formData, foods: updatedFoods });
  };

  const removeFood = (index) => {
    const updatedFoods = formData.foods.filter((_, i) => i !== index);
    setFormData({ ...formData, foods: updatedFoods });
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 py-8">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Please log in to view meal plans</h1>
          <button
            onClick={() => navigate('/login')}
            className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            Log In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <button
          onClick={() => navigate('/app/nutrition')}
          className="flex items-center space-x-2 text-purple-600 hover:text-purple-800 mb-6"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back to Nutrition</span>
        </button>

        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2 flex items-center">
              <BookOpen className="w-10 h-10 mr-3 text-purple-600" />
              My Meal Plans
            </h1>
            <p className="text-xl text-gray-600">
              Custom feeding plans for your pets
            </p>
          </div>
          <button
            onClick={() => setShowCreateForm(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            <Plus className="w-5 h-5" />
            <span>Create Meal Plan</span>
          </button>
        </div>

        {showCreateForm && (
          <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
            <h2 className="text-2xl font-semibold mb-6">Create New Meal Plan</h2>
            <form onSubmit={handleCreateMealPlan} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Pet Name
                  </label>
                  <input
                    type="text"
                    value={formData.pet_name}
                    onChange={(e) => setFormData({ ...formData, pet_name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Weight (lbs)
                  </label>
                  <input
                    type="number"
                    value={formData.pet_weight}
                    onChange={(e) => setFormData({ ...formData, pet_weight: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Age (months)
                  </label>
                  <input
                    type="number"
                    value={formData.pet_age_months}
                    onChange={(e) => setFormData({ ...formData, pet_age_months: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Activity Level
                  </label>
                  <select
                    value={formData.activity_level}
                    onChange={(e) => setFormData({ ...formData, activity_level: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="low">Low</option>
                    <option value="moderate">Moderate</option>
                    <option value="high">High</option>
                    <option value="very_high">Very High</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Special Needs (optional)
                </label>
                <textarea
                  value={formData.special_needs}
                  onChange={(e) => setFormData({ ...formData, special_needs: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  rows="3"
                  placeholder="Any allergies, health conditions, or special dietary requirements..."
                />
              </div>

              <div>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium">Foods</h3>
                  <button
                    type="button"
                    onClick={addFoodToMealPlan}
                    className="text-purple-600 hover:text-purple-800"
                  >
                    + Add Food
                  </button>
                </div>
                
                {formData.foods.map((food, index) => (
                  <div key={index} className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4 p-4 bg-gray-50 rounded-lg">
                    <select
                      value={food.food_id}
                      onChange={(e) => updateFood(index, 'food_id', e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      required
                    >
                      <option value="">Select Food</option>
                      {foods.map(f => (
                        <option key={f.id} value={f.id}>{f.name}</option>
                      ))}
                    </select>
                    <select
                      value={food.meal_time}
                      onChange={(e) => updateFood(index, 'meal_time', e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="morning">Morning</option>
                      <option value="midday">Midday</option>
                      <option value="evening">Evening</option>
                      <option value="bedtime">Bedtime</option>
                    </select>
                    <input
                      type="number"
                      step="0.25"
                      placeholder="Amount (cups)"
                      value={food.amount}
                      onChange={(e) => updateFood(index, 'amount', e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => removeFood(index)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                ))}
              </div>

              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateForm(false);
                    resetForm();
                  }}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  Create Meal Plan
                </button>
              </div>
            </form>
          </div>
        )}

        {loading ? (
          <LoadingSpinner />
        ) : mealPlans.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <Dog className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No meal plans yet</h3>
            <p className="text-gray-600">Create your first meal plan to get started!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {mealPlans.map((plan) => (
              <div key={plan.id} className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 flex items-center">
                      <Dog className="w-5 h-5 mr-2 text-purple-600" />
                      {plan.pet_name}
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">
                      Created {new Date(plan.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3 mb-4">
                  <div className="text-center p-2 bg-gray-50 rounded">
                    <Scale className="w-4 h-4 mx-auto text-gray-500 mb-1" />
                    <div className="text-sm font-medium">{plan.pet_weight} lbs</div>
                  </div>
                  <div className="text-center p-2 bg-gray-50 rounded">
                    <Clock className="w-4 h-4 mx-auto text-gray-500 mb-1" />
                    <div className="text-sm font-medium">{plan.pet_age_months} months</div>
                  </div>
                  <div className="text-center p-2 bg-gray-50 rounded">
                    <Activity className="w-4 h-4 mx-auto text-gray-500 mb-1" />
                    <div className="text-sm font-medium">{plan.activity_level}</div>
                  </div>
                </div>

                {plan.special_needs && (
                  <div className="mb-4 p-3 bg-yellow-50 rounded-lg">
                    <p className="text-sm text-yellow-800">
                      <strong>Special Needs:</strong> {plan.special_needs}
                    </p>
                  </div>
                )}

                <div className="border-t pt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">
                      {plan.food_count || 0} foods • {plan.total_daily_amount || 0} cups/day
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default MealPlans;